import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog.jsx";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import StatusTag from "./StatusTag";
import { api } from "./ProfessionalContacts.helper";
import {
  piiStatus,
  getMainContact,
  getPreferredAddress,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import { firmTypeLabel } from "../../../../../domain/professionalContacts/professionalContacts.types";
import useStyles from "./ProfessionalContacts.styles";

/**
 * FIRM-DUPLICATE-NAMES-009: the company the Add/Edit form's duplicate-name warning is talking
 * about, shown without leaving the form.
 *
 * READ-ONLY, and deliberately not FirmRecord (HLD §11, 2026-09-23). This floats over a form
 * holding unsaved work, so it carries no Edit, no Delete, no flag switches and no route into the
 * full record — a destructive or navigating action one click from a half-finished draft is exactly
 * what the dialog exists to avoid. It answers only the question the warning raises: is this the
 * same company?
 *
 * The Professional Contacts screen is one route with no per-firm URL (src/Routes/routes.js:46),
 * which is why the alternative — navigating to the record — would have discarded the draft.
 */
const ExistingFirmDialog = ({ open, firmId, onClose }) => {
  const classes = useStyles();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !firmId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setFirm(null);

    api
      .getFirm(firmId)
      .then((result) => {
        if (!cancelled) setFirm(result);
      })
      .catch((err) => {
        // Shown INSIDE the dialog. The form behind it is untouched and stays saveable — a firm
        // we could not display is not a reason to interfere with what the user was doing.
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, firmId]);

  const mainContact = getMainContact(firm);
  const preferredAddress = getPreferredAddress(firm);

  const addressLine = preferredAddress
    ? [
        preferredAddress.building,
        preferredAddress.street,
        preferredAddress.town,
        preferredAddress.postcode,
        preferredAddress.country,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  const detail = (label, value) =>
    value ? (
      <div className={classes.existingFirmRow}>
        <span className={classes.existingFirmLabel}>{label}</span>
        <span className={classes.existingFirmValue}>{value}</span>
      </div>
    ) : null;

  return (
    <A_SimpleDialog
      open={open}
      onClose={onClose}
      title={
        <span className={classes.dialogTitleText}>
          {firm ? firm.name : "Existing company"}
        </span>
      }
      maxWidth="sm"
      fullWidth
      dialogContent={
        <div className={classes.existingFirmBody}>
          {loading && <LoaderSkeleton />}
          <M_DataStateRenderer loading={false} error={error} data={firm}>
            {firm && (
              <>
                <div className={classes.existingFirmTags}>
                  <StatusTag tone="neutral">{firmTypeLabel(firm.type)}</StatusTag>
                  <StatusTag tone={firm.active ? "valid" : "danger"}>
                    {firm.active ? "Active" : "Inactive"}
                  </StatusTag>
                  <StatusTag
                    tone={piiStatus(firm) === "Valid" ? "valid" : "expiring"}
                  >
                    PII: {piiStatus(firm)}
                  </StatusTag>
                  {firm.noFurtherInstructions && (
                    <StatusTag tone="danger">No further instructions</StatusTag>
                  )}
                </div>

                {detail("Main contact number", firm.mainPhone)}
                {detail("Company email", firm.email)}
                {detail(
                  "Main individual",
                  mainContact
                    ? [mainContact.name, mainContact.email, mainContact.phone]
                        .filter(Boolean)
                        .join(" · ")
                    : null
                )}
                {detail("Preferred address", addressLine)}
                {detail(
                  "Individuals",
                  String((firm.contacts || []).length)
                )}
                {detail("Addresses", String((firm.addresses || []).length))}

                <div className={classes.existingFirmHint}>
                  Shown so you can check this isn&apos;t the same company. Close
                  to return to the form — nothing you&apos;ve entered is lost.
                </div>
              </>
            )}
          </M_DataStateRenderer>
        </div>
      }
      // An explicit Close, as FirmDocumentsAllDialog does. A_SimpleDialog's own close icon is
      // reachable but unlabelled — it passes aria-label to A_IconButton, which reads ariaLabel,
      // so that button announces as "icon button". Not this task's to fix; this sidesteps it.
      dialogActions={
        <A_Button label="Close" premiumVariant="secondary" onClick={onClose} />
      }
    />
  );
};

ExistingFirmDialog.propTypes = {
  open: PropTypes.bool,
  firmId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ExistingFirmDialog.defaultProps = {
  open: false,
  firmId: null,
};

export default ExistingFirmDialog;
